import React, { FC, memo, useContext, useEffect, useState } from 'react'

import { Select } from 'antd'
import { debounce } from 'lodash-es'
import { useFormContext } from 'react-hook-form'
import { WizardContext } from 'react-wizard-primitive'

import { AppContext } from 'Const'
import useEvent from 'Hooks/useEvent'
import useToggle from 'Hooks/useToggle'
import { isShipping } from 'Utils/cartFulfillment'
import { fetchCities, SuggestedOption } from 'Utils/google'

const DELIMITER = '###'

interface Option extends SuggestedOption {
  cityName: string
}

interface Props {
  calcTransportCost: () => void
  className?: string
  id?: string
  nameGroupPrefix: string
}

const CitySelect: FC<Props> = ({ calcTransportCost, className, nameGroupPrefix, ...props }) => {
  const { activeStepIndex } = useContext(WizardContext) || {}

  const { googlePlacesApiKey } = useContext(AppContext)
  const { setValue, register, watch } = useFormContext()
  const [options, setOptions] = useState([] as Option[])
  const [loading, , loadStatus, loadedStatus] = useToggle()
  const [open, toggleOpen] = useToggle()

  const name = `${nameGroupPrefix}[city]`
  const countryFieldName = `${nameGroupPrefix}[countryIsoCode]`
  const cityGooglePlaceIdFieldName = `${nameGroupPrefix}[cityGooglePlaceId]`

  const city = watch(name)
  const cityGooglePlaceId = watch(cityGooglePlaceIdFieldName)
  const countryIsoCode = watch(countryFieldName)
  const value = city && cityGooglePlaceId ? `${city}${DELIMITER}${cityGooglePlaceId}` : undefined

  useEffect(() => {
    register({ name }, { required: 'City is required' })
    register({ name: cityGooglePlaceIdFieldName }, { required: "Sorry, city isn't supported" })
  }, [cityGooglePlaceIdFieldName, name, register])

  useEffect(() => {
    if (value && options.every((option) => option.value != value)) {
      setOptions((options) => [
        { value: value || '', cityName: city, label: city, placeId: cityGooglePlaceId },
        ...options,
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useEffect(() => {
    if (!cityGooglePlaceId) {
      toggleOpen(true)
      if (!options.length) {
        void handleSearch(city)
      }
    }
  }, [activeStepIndex])

  // TODO: clear value only on user change countryIsoCode
  // useUpdateEffect(() => {
  //   setValue(name, null)
  //   setValue(cityGooglePlaceIdFieldName, null)
  // }, [countryIsoCode, name, setValue, cityGooglePlaceIdFieldName])

  const handleChange = useEvent((value) => {
    const [cityName, placeId] = (value as string).split(DELIMITER)
    setValue(name, cityName)
    setValue(cityGooglePlaceIdFieldName, placeId)
    calcTransportCost()
  })

  const handleSearch = useEvent(
    debounce(async (query: string) => {
      if (countryIsoCode) {
        loadStatus()
        const cities = await fetchCities(countryIsoCode, query, googlePlacesApiKey)
        loadedStatus()
        setOptions(
          cities.map((city) => ({ ...city, cityName: city.value, value: `${city.value}${DELIMITER}${city.placeId}` })),
        )
      }
    }, 500),
  )

  return (
    <Select<string>
      showSearch
      className={`w-100 ${className}`}
      disabled={!countryIsoCode}
      filterOption={false}
      loading={loading}
      open={open}
      optionFilterProp="label"
      optionLabelProp="cityName"
      options={options}
      placeholder="Select your city"
      size="large"
      value={value}
      onChange={handleChange}
      onDropdownVisibleChange={toggleOpen}
      onSearch={handleSearch}
      {...props}
    />
  )
}

export default memo(CitySelect)
